import { Commentary, CommentaryV2, isDefined } from '@aloft/toolkit-shared';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { CommentaryState, SyncResult } from '../types.ts';
import { clearOrder } from './actions/clearOrder.ts';
import { submitReconciliation, submitReconciliationDataSheet } from './reconciliationSlice.ts';
import { getBumpedSyncState } from './utils/getBumpedSyncState.ts';

export const initialCommentaryState: CommentaryState = {
  marketConditionsV2: {
    filter: {
      type: 'Zip Code',
    },
  },
};

const commentarySlice = createSlice({
  name: 'commentary',
  initialState: initialCommentaryState,
  reducers: {
    setCommentary: (state, action: PayloadAction<Commentary>) => {
      Object.assign(state, {
        ...action.payload,
      });
    },
    setCommentaryV2: (state, action: PayloadAction<CommentaryV2>) => {
      Object.assign(state, {
        ...action.payload,
        syncState: getBumpedSyncState(state.syncState),
      });
    },
    commentaryMutationComplete(state, action: PayloadAction<SyncResult>) {
      const { payload } = action;
      const { status } = payload;

      if (state.syncState) {
        Object.assign(state.syncState, { status });

        if (status === 'success') {
          Object.assign(state.syncState, { serverRevision: payload.serverRevision });
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearOrder, () => initialCommentaryState);
    builder.addCase(submitReconciliationDataSheet, (state, action) => {
      const { reconciliationCommentary, aivReconciliationCommentary, arvReconciliationCommentary } =
        action.payload;
      const hasCommentaryUpdate =
        isDefined(reconciliationCommentary) ||
        isDefined(aivReconciliationCommentary) ||
        isDefined(arvReconciliationCommentary);
      if (hasCommentaryUpdate) {
        const reconciliationUpdates: Partial<NonNullable<(typeof state)['reconciliation']>> = {};
        if (isDefined(aivReconciliationCommentary)) {
          reconciliationUpdates.aivReconciliationCommentary = aivReconciliationCommentary;
          reconciliationUpdates.reconciliationCommentary = aivReconciliationCommentary;
        } else if (isDefined(reconciliationCommentary)) {
          reconciliationUpdates.reconciliationCommentary = reconciliationCommentary;
        }
        if (isDefined(arvReconciliationCommentary)) {
          reconciliationUpdates.arvReconciliationCommentary = arvReconciliationCommentary;
        }
        Object.assign(state, {
          syncState: getBumpedSyncState(state.syncState),
          reconciliation: {
            ...state.reconciliation,
            ...reconciliationUpdates,
          },
        });
      }
    });
    builder.addCase(submitReconciliation, (state, action) => {
      const {
        totalCostToCureCommentary,
        reconciliationCommentary,
        aivReconciliationCommentary,
        arvReconciliationCommentary,
        highestAndBestUseCommentary,
        revisionCommentary,
        alternativeComparablesCommentary,
        feasibilityCommentary,
        aivDiscrepancyCommentary,
        arvDiscrepancyCommentary,
      } = action.payload;

      const hasSplitCommentary =
        isDefined(aivReconciliationCommentary) && isDefined(arvReconciliationCommentary);
      const hasSingleCommentary = isDefined(reconciliationCommentary) && !hasSplitCommentary;
      const shouldUpdateMainFields =
        (hasSplitCommentary || hasSingleCommentary) && isDefined(highestAndBestUseCommentary);
      const shouldUpdateFeasibility = 'feasibilityCommentary' in action.payload;

      if (shouldUpdateMainFields || shouldUpdateFeasibility) {
        const updates: Partial<typeof state.reconciliation> = {};

        if (shouldUpdateMainFields) {
          updates.totalCostToCureCommentary = totalCostToCureCommentary;
          updates.highestAndBestUseCommentary = highestAndBestUseCommentary;
          updates.revisionCommentary = revisionCommentary;
          updates.alternativeComparablesCommentary = alternativeComparablesCommentary;
          updates.aivDiscrepancyCommentary = aivDiscrepancyCommentary;
          updates.arvDiscrepancyCommentary = arvDiscrepancyCommentary;
          if (hasSplitCommentary) {
            updates.aivReconciliationCommentary = aivReconciliationCommentary;
            updates.arvReconciliationCommentary = arvReconciliationCommentary;
            updates.reconciliationCommentary = aivReconciliationCommentary;
          } else {
            updates.reconciliationCommentary = reconciliationCommentary;
          }
        }

        if (shouldUpdateFeasibility) {
          updates.feasibilityCommentary = feasibilityCommentary ?? '';
        }

        Object.assign(state, {
          syncState: getBumpedSyncState(state.syncState),
          reconciliation: {
            ...state.reconciliation,
            ...updates,
          },
        });
      }
    });
  },
});

export const { setCommentary, setCommentaryV2, commentaryMutationComplete } =
  commentarySlice.actions;

export const commentaryReducer = commentarySlice.reducer;
